import { ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { BoxWithLogo, FlexBox } from 'components/Box'
import { LastModifiedCell } from 'components/Table'
import { COLUMN_SIZE } from 'components/Table/constants'
import { bidderLogos } from 'constants/bidderLogos'
import { Alert } from 'features/alerts/types/alert'
import { DomainBidderAlert } from 'features/globalSetup/api/types/domainBidderAlert'
import { DomainBidderSlice } from 'features/inventory/domain/forms/PrebidStackForm'
import { DomainBidderAlerts } from '../DomainBidderAlerts'
import { ActionsCell } from './components/ActionsCell'
import { CsEnabledCell } from './components/CsEnabledCell'
import { PriorityCell } from './components/PriorityCell'
import { S2sEnabledCell } from './components/S2sEnabledCell'

export const useColumns = (): ColumnDef<DomainBidderSlice>[] => {
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.prebidStack.bidders.list.header',
  })

  return [
    {
      accessorKey: 'name',
      cell: ({
        row: {
          original: { name },
        },
      }) => <BoxWithLogo logo={{ size: 'small', svg: bidderLogos[name] }}>{name}</BoxWithLogo>,
      header: t('name'),
    },

    {
      accessorKey: 'code',
      header: t('code.title'),
      meta: { tooltip: { content: t('code.tooltip') } },
    },

    {
      accessorKey: 'priority',
      cell: props => <PriorityCell {...props} />,
      header: t('priority.title'),
      meta: {
        noMemo: true,
        nonClickable: true,
        tooltip: { content: t('priority.tooltip') },
      },
    },

    {
      accessorKey: 'csEnabled',
      cell: ({
        row: {
          original: { alerts, id, status },
        },
      }) => (
        <FlexBox axis='x'>
          <CsEnabledCell {...{ id, status }} />

          {alerts.length > 0 && (
            <DomainBidderAlerts
              alerts={alerts.filter((alert: Alert<DomainBidderAlert>): boolean =>
                alert.name.startsWith('cs'),
              )}
            />
          )}
        </FlexBox>
      ),
      header: t('csEnabled.title'),
      meta: {
        centered: true,
        noMemo: true,
        nonClickable: true,
        tooltip: { content: t('csEnabled.tooltip') },
      },
    },

    {
      accessorKey: 's2sEnabled',
      cell: props => {
        const {
          row: {
            original: { alerts, id, s2sAvailable, status },
          },
        } = props

        return (
          s2sAvailable && (
            <FlexBox axis='x'>
              <S2sEnabledCell {...{ id, status }} />

              {alerts.length > 0 && (
                <DomainBidderAlerts
                  alerts={alerts.filter((alert: Alert<DomainBidderAlert>): boolean =>
                    alert.name.startsWith('s2s'),
                  )}
                />
              )}
            </FlexBox>
          )
        )
      },
      header: t('s2sEnabled.title'),
      meta: {
        centered: true,
        noMemo: true,
        nonClickable: true,
        tooltip: { content: t('s2sEnabled.tooltip') },
      },
    },

    {
      accessorKey: 'updatedAt',
      cell: props => <LastModifiedCell {...props} />,
      header: t('updatedAt'),
    },

    {
      accessorKey: 'id',
      cell: props => <ActionsCell {...props} />,
      header: t('actions'),
      meta: { nonClickable: true },
      size: COLUMN_SIZE.SM,
    },
  ]
}
